// Enhanced API service with hash-based caching for all endpoints
import { getCurrentConfig, getApiScope } from '../config';
import { 
  Terminal, Checkpoint, Section, LaneType, User,
  LaneFormData, TerminalFormData, CheckpointFormData, SectionFormData, LaneTypeFormData
} from '../types';
import { LaneAssignment } from '../services/laneAssignmentService';
import { API_ENDPOINTS, DEFAULT_HEADERS } from '../config/constants';

interface HashResponse<T> {
  success: boolean;
  message: string;
  timestamp: string;
  data?: T;
  hash?: string;
  changed?: boolean;
  error?: string | null;
}

interface CachedEndpoint<T> {
  data: T | null;
  hash: string | null;
  lastFetch: Date | null;
}

class EnhancedApiService {
  private baseURL: string;
  private cache: Map<string, CachedEndpoint<any>> = new Map();
  private accessTokenGetter: (() => Promise<string | null>) | null = null;

  constructor() {
    this.baseURL = getCurrentConfig().apiBaseUrl;
  }
  

  // Set access token getter (will be called from components with auth context)
  setAccessTokenGetter(getter: () => Promise<string | null>) {
    this.accessTokenGetter = getter;
  }

  // Generic HTTP request method with inline MSAL authentication
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    try {
      const baseUrl = this.baseURL;
      
      // Fix double slash issue - simple URL construction
      let url = baseUrl + endpoint;
      url = url.replace(/\/+/g, '/'); // Replace multiple slashes with single slash
      url = url.replace(':/', '://'); // Fix protocol if we accidentally broke it
      
      const headers: Record<string, string> = {
        ...DEFAULT_HEADERS,
      };

      // INLINE TOKEN ACQUISITION - Like universalApiService and other services
      const config = getCurrentConfig();
      const authEnabled = config.auth.enableAuthentication;
      
      // Enhanced API auth check completed
      
      if (authEnabled) {
        try {
          // Enhanced API: Acquiring token inline
          const { msalInstance } = await import('../contexts/AuthContext');
          const accounts = msalInstance.getAllAccounts();
          
          if (accounts.length > 0) {
            const tokenResponse = await msalInstance.acquireTokenSilent({
              scopes: [getApiScope()], // Use scope from .env
              account: accounts[0]
            });
            headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
            // Enhanced API: Using inline token
          } else {
            // Enhanced API: No accounts available for token
          }
        } catch (error) {
          console.warn('❌ Enhanced API: Inline token acquisition failed:', error);
        }
      } else {
        // Enhanced API: Auth disabled
      }

      // Add any additional headers from options
      if (options?.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (typeof value === 'string') {
            headers[key] = value;
          }
        });
      }

      const response = await fetch(url, {
        headers,
        ...options,
      });

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          throw new Error('Authentication required - please log in again');
        }
        if (response.status === 403) {
          throw new Error('Access denied - insufficient permissions');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  }

  // Generic hash-based fetch for any endpoint
  private async fetchWithHash<T>(
    endpoint: string,
    cacheKey: string,
    forceRefresh = false
  ): Promise<T> {
    const cached = this.cache.get(cacheKey);
    
    // Return cached data if fresh and not forcing refresh
    if (!forceRefresh && cached?.data && this.isDataFresh(cached.lastFetch)) {
      return cached.data;
    }

    try {
      let url = endpoint;
      
      // Add hash parameter if we have cached hash
      if (cached?.hash && !forceRefresh) {
        const separator = endpoint.includes('?') ? '&' : '?';
        url = `${endpoint}${separator}hash=${cached.hash}`;
      }

      const response = await this.request<HashResponse<T>>(url);

      if (response.success) {
        // Check if data changed
        if (response.changed === false && cached?.data) {
          // No changes, update timestamp but keep existing data
          cached.lastFetch = new Date();
          return cached.data;
        }

        // Data changed or first fetch, update cache
        if (response.data) {
          this.cache.set(cacheKey, {
            data: response.data,
            hash: response.hash || null,
            lastFetch: new Date()
          });
          return response.data;
        }
      }

      throw new Error(response.error || 'Failed to fetch data');
    } catch (error) {
      throw error;
    }
  }

  // Check if cached data is fresh (within 2 minutes)
  private isDataFresh(lastFetch: Date | null): boolean {
    if (!lastFetch) return false;
    const twoMinutes = 2 * 60 * 1000;
    const now = new Date();
    return (now.getTime() - lastFetch.getTime()) < twoMinutes;
  }

  // Terminals with hash support
  async getTerminals(forceRefresh = false): Promise<Terminal[]> {
    return this.fetchWithHash<Terminal[]>('/terminals/', 'terminals', forceRefresh);
  }

  async getTerminal(id: number): Promise<Terminal> {
    return this.request<Terminal>(`/terminals/${id}`);
  }

  async createTerminal(data: TerminalFormData): Promise<Terminal> {
    const result = await this.request<Terminal>('/terminals/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Clear cache to force refresh on next fetch
    this.cache.delete('terminals');
    return result;
  }

  async updateTerminal(id: number, data: Partial<TerminalFormData>): Promise<Terminal> {
    const result = await this.request<Terminal>(`/terminals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Clear cache to force refresh on next fetch
    this.cache.delete('terminals');
    return result;
  }

  async deleteTerminal(id: number): Promise<{ message: string }> {
    const result = await this.request<{ message: string }>(`/terminals/${id}`, { 
      method: 'DELETE' 
    });
    // Clear cache to force refresh on next fetch
    this.cache.delete('terminals');
    return result;
  }

  // Checkpoints with hash support
  async getCheckpoints(terminalId?: number, forceRefresh = false): Promise<Checkpoint[]> {
    const endpoint = terminalId ? `/checkpoints/?terminal_id=${terminalId}` : '/checkpoints/';
    const cacheKey = terminalId ? `checkpoints_terminal_${terminalId}` : 'checkpoints';
    return this.fetchWithHash<Checkpoint[]>(endpoint, cacheKey, forceRefresh);
  }

  async getCheckpoint(id: number): Promise<Checkpoint> {
    return this.request<Checkpoint>(`/checkpoints/${id}`);
  }

  async createCheckpoint(data: CheckpointFormData): Promise<Checkpoint> {
    const result = await this.request<Checkpoint>('/checkpoints/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Clear relevant caches
    this.cache.delete('checkpoints');
    this.cache.delete(`checkpoints_terminal_${data.terminal_id}`);
    return result;
  }

  async updateCheckpoint(id: number, data: Partial<CheckpointFormData>): Promise<Checkpoint> {
    const result = await this.request<Checkpoint>(`/checkpoints/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Clear relevant caches
    this.cache.delete('checkpoints');
    if (data.terminal_id) {
      this.cache.delete(`checkpoints_terminal_${data.terminal_id}`);
    }
    return result;
  }

  async deleteCheckpoint(id: number): Promise<{ message: string }> {
    const result = await this.request<{ message: string }>(`/checkpoints/${id}`, { 
      method: 'DELETE' 
    });
    // Clear all checkpoint caches
    Array.from(this.cache.keys())
      .filter(key => key.startsWith('checkpoints'))
      .forEach(key => this.cache.delete(key));
    return result;
  }

  // Sections with hash support
  async getSections(checkpointId?: number, forceRefresh = false): Promise<Section[]> {
    const endpoint = checkpointId ? `/sections/?checkpoint_id=${checkpointId}` : '/sections/';
    const cacheKey = checkpointId ? `sections_checkpoint_${checkpointId}` : 'sections';
    
    // Special handling for sections API which returns nested data.sections
    const cached = this.cache.get(cacheKey);
    
    if (!forceRefresh && cached?.data && this.isDataFresh(cached.lastFetch)) {
      return cached.data;
    }

    try {
      let url = endpoint;
      if (cached?.hash && !forceRefresh) {
        const separator = endpoint.includes('?') ? '&' : '?';
        url = `${endpoint}${separator}hash=${cached.hash}`;
      }

      const response = await this.request<{
        success: boolean;
        data: { sections: Section[] };
        hash?: string;
        changed?: boolean;
        error?: string | null;
      }>(url);

      if (response.success) {
        if (response.changed === false && cached?.data) {
          cached.lastFetch = new Date();
          return cached.data;
        }

        // Extract sections from nested data.sections
        const sections = response.data?.sections || [];
        this.cache.set(cacheKey, {
          data: sections,
          hash: response.hash || null,
          lastFetch: new Date()
        });
        return sections;
      }

      throw new Error(response.error || 'Failed to fetch sections');
    } catch (error) {
      console.error('Failed to fetch sections:', error);
      throw error;
    }
  }

  async getSection(id: number): Promise<Section> {
    return this.request<Section>(`/sections/${id}`);
  }

  async createSection(data: SectionFormData): Promise<Section> {
    const result = await this.request<Section>('/sections/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Clear relevant caches
    this.cache.delete('sections');
    this.cache.delete(`sections_checkpoint_${data.checkpoint_id}`);
    return result;
  }

  async updateSection(id: number, data: Partial<SectionFormData>): Promise<Section> {
    const result = await this.request<Section>(`/sections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Clear relevant caches
    this.cache.delete('sections');
    if (data.checkpoint_id) {
      this.cache.delete(`sections_checkpoint_${data.checkpoint_id}`);
    }
    return result;
  }

  async deleteSection(id: number): Promise<{ message: string }> {
    const result = await this.request<{ message: string }>(`/sections/${id}`, { 
      method: 'DELETE' 
    });
    // Clear all sections caches
    Array.from(this.cache.keys())
      .filter(key => key.startsWith('sections'))
      .forEach(key => this.cache.delete(key));
    return result;
  }

  // Lane Types with hash support - Custom handling for nested response
  async getLaneTypes(checkpointId?: number, purpose?: string, forceRefresh = false): Promise<LaneType[]> {
    let endpoint = '/lanetypes/';
    const params = new URLSearchParams();
    
    if (checkpointId) params.append('checkpoint_id', checkpointId.toString());
    if (purpose) params.append('purpose', purpose);
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }

    const cacheKey = `lanetypes_${checkpointId || 'all'}_${purpose || 'all'}`;
    
    // getLaneTypes called
    
    // Custom handling for lane types API response structure
    const cached = this.cache.get(cacheKey);
    // Cache status checked
    
    // Return cached data if fresh and not forcing refresh
    if (!forceRefresh && cached?.data && this.isDataFresh(cached.lastFetch)) {
      // Returning cached lane types
      // Ensure we return the correct format from cache
      if (Array.isArray(cached.data)) {
        return cached.data;
      } else if (cached.data.lane_types && Array.isArray(cached.data.lane_types)) {
        // Extracting lane_types from cached data
        return cached.data.lane_types;
      }
      return cached.data;
    }

    try {
      let url = endpoint;
      
      // Add hash parameter if we have cached hash
      if (cached?.hash && !forceRefresh) {
        const separator = endpoint.includes('?') ? '&' : '?';
        url = `${endpoint}${separator}hash=${cached.hash}`;
      }

      // Making API request
      const response = await this.request<any>(url);
      // API response received

      if (response.success && response.data) {
        // Extract lane_types from nested data structure
        const laneTypes = response.data.lane_types || [];
        // Extracted lane types
        
        // Update cache
        this.cache.set(cacheKey, {
          data: laneTypes,
          hash: response.data.hash || null,
          lastFetch: new Date()
        });
        
        // Lane Types loaded and cached
        return laneTypes;
      } else if (response.lane_types) {
        // Handle case where response is the data object itself
        const laneTypes = response.lane_types || [];
        // Extracted lane types from direct response
        
        // Update cache
        this.cache.set(cacheKey, {
          data: laneTypes,
          hash: response.hash || null,
          lastFetch: new Date()
        });
        
        // Lane Types loaded and cached (direct)
        return laneTypes;
      }

      console.error('❌ Invalid response structure:', response);
      throw new Error(response.error || 'Failed to fetch lane types');
    } catch (error) {
      console.error(`❌ Error fetching lane types:`, error);
      throw error;
    }
  }

  async getLaneType(id: number): Promise<LaneType> {
    return this.request<LaneType>(`/lanetypes/${id}`);
  }

  async createLaneType(data: LaneTypeFormData): Promise<LaneType> {
    const result = await this.request<LaneType>('/lanetypes/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Clear relevant caches
    Array.from(this.cache.keys())
      .filter(key => key.startsWith('lanetypes'))
      .forEach(key => this.cache.delete(key));
    return result;
  }

  async updateLaneType(id: number, data: Partial<LaneTypeFormData>): Promise<LaneType> {
    const result = await this.request<LaneType>(`/lanetypes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Clear relevant caches
    Array.from(this.cache.keys())
      .filter(key => key.startsWith('lanetypes'))
      .forEach(key => this.cache.delete(key));
    return result;
  }

  async deleteLaneType(id: number): Promise<{ message: string }> {
    const result = await this.request<{ message: string }>(`/lanetypes/${id}`, { 
      method: 'DELETE' 
    });
    // Clear all lane types caches
    Array.from(this.cache.keys())
      .filter(key => key.startsWith('lanetypes'))
      .forEach(key => this.cache.delete(key));
    return result;
  }

  // Toggle operations with proper parameters
  async toggleCheckpoint(
    id: number, 
    cascade = true, 
    updatedBy = 'api_user', 
    reason?: string
  ): Promise<{ message: string }> {
    const params = new URLSearchParams();
    params.append('cascade', cascade.toString());
    params.append('updated_by', updatedBy);
    if (reason) params.append('reason', reason);

    const result = await this.request<{ message: string }>(
      `/checkpoints/${id}/toggle?${params.toString()}`, 
      { method: 'PUT' }
    );

    // Clear all caches after checkpoint toggle to ensure fresh data
    this.cache.clear();

    return result;
  }

  async toggleTerminal(
    id: number, 
    cascade = true, 
    updatedBy = 'api_user', 
    reason?: string
  ): Promise<{ message: string }> {
    const params = new URLSearchParams();
    params.append('cascade', cascade.toString());
    params.append('updated_by', updatedBy);
    if (reason) params.append('reason', reason);

    const result = await this.request<{ message: string }>(
      `/terminals/${id}/toggle?${params.toString()}`, 
      { method: 'PUT' }
    );

    // Clear all caches after terminal toggle (affects everything)
    this.cache.clear();

    return result;
  }

  async toggleSection(
    id: number, 
    cascade = true, 
    updatedBy = 'api_user', 
    reason?: string
  ): Promise<{ message: string }> {
    const params = new URLSearchParams();
    params.append('cascade', cascade.toString());
    params.append('updated_by', updatedBy);
    if (reason) params.append('reason', reason);

    const result = await this.request<{ message: string }>(
      `/sections/${id}/toggle?${params.toString()}`, 
      { method: 'PUT' }
    );

    // Clear section-related caches
    Array.from(this.cache.keys())
      .filter(key => key.includes('section'))
      .forEach(key => this.cache.delete(key));

    return result;
  }

  async toggleLaneAssignment(
    id: number, 
    cascade = true, 
    updatedBy = 'api_user', 
    reason?: string
  ): Promise<{ message: string }> {
    const params = new URLSearchParams();
    params.append('cascade', cascade.toString());
    params.append('updated_by', updatedBy);
    if (reason) params.append('reason', reason);

    const result = await this.request<{ message: string }>(
      `/laneassignments/${id}/toggle?${params.toString()}`, 
      { method: 'PUT' }
    );

    // Clear all dashboard-related caches after lane assignment toggle
    this.cache.clear(); // Clear everything to ensure fresh data

    return result;
  }

  // ============================================================================
  // USER MANAGEMENT METHODS
  // ============================================================================

  async getUsers(forceRefresh = false): Promise<User[]> {
    const cacheKey = 'users_all';
    return this.fetchWithHash<User[]>('/users/', cacheKey, forceRefresh);
  }

  async getUser(id: number): Promise<User> {
    return this.request<User>(`/users/${id}`);
  }

  async createUser(data: Partial<User>): Promise<User> {
    const result = await this.request<User>('/users/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Clear users cache
    Array.from(this.cache.keys())
      .filter(key => key.startsWith('users'))
      .forEach(key => this.cache.delete(key));
    return result;
  }

  async updateUser(id: number, data: Partial<User>): Promise<User> {
    const result = await this.request<User>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Clear users cache
    Array.from(this.cache.keys())
      .filter(key => key.startsWith('users'))
      .forEach(key => this.cache.delete(key));
    return result;
  }

  async deleteUser(id: number): Promise<{ message: string }> {
    const result = await this.request<{ message: string }>(`/users/${id}`, {
      method: 'DELETE',
    });
    // Clear users cache
    Array.from(this.cache.keys())
      .filter(key => key.startsWith('users'))
      .forEach(key => this.cache.delete(key));
    return result;
  }

  // ============================================================================
  // LANE ASSIGNMENT MANAGEMENT METHODS  
  // ============================================================================

  async getLaneAssignments(checkpointId?: number, forceRefresh = false): Promise<LaneAssignment[]> {
    let endpoint = '/laneassignments/';
    const cacheKey = `laneassignments_${checkpointId || 'all'}`;
    
    if (checkpointId) {
      endpoint += `?checkpoint_id=${checkpointId}`;
    }
    
    return this.fetchWithHash<LaneAssignment[]>(endpoint, cacheKey, forceRefresh);
  }

  async getLaneAssignment(id: number): Promise<LaneAssignment> {
    return this.request<LaneAssignment>(`/laneassignments/${id}`);
  }

  async createLaneAssignment(data: Partial<LaneAssignment>): Promise<LaneAssignment> {
    const result = await this.request<LaneAssignment>('/laneassignments/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Clear lane assignments cache
    Array.from(this.cache.keys())
      .filter(key => key.startsWith('laneassignments'))
      .forEach(key => this.cache.delete(key));
    return result;
  }

  async updateLaneAssignment(id: number, data: Partial<LaneAssignment>): Promise<LaneAssignment> {
    const result = await this.request<LaneAssignment>(`/laneassignments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Clear lane assignments cache
    Array.from(this.cache.keys())
      .filter(key => key.startsWith('laneassignments'))
      .forEach(key => this.cache.delete(key));
    return result;
  }

  async deleteLaneAssignment(id: number): Promise<{ message: string }> {
    const result = await this.request<{ message: string }>(`/laneassignments/${id}`, {
      method: 'DELETE',
    });
    // Clear lane assignments cache
    Array.from(this.cache.keys())
      .filter(key => key.startsWith('laneassignments'))
      .forEach(key => this.cache.delete(key));
    return result;
  }

  // ============================================================================
  // CACHE MANAGEMENT METHODS
  // ============================================================================

  // Clear all cached data
  clearAllCache(): void {
    this.cache.clear();
  }

  // Clear all cache
  clearCache() {
    // Clearing all cache
    this.cache.clear();
  }

  // Clear specific cache key
  clearCacheKey(key: string) {
    // Clearing cache for key
    this.cache.delete(key);
  }
  getCacheInfo(): Record<string, any> {
    const info: Record<string, any> = {};
    this.cache.forEach((value, key) => {
      info[key] = {
        hasData: !!value.data,
        hash: value.hash?.substring(0, 8) + '...',
        lastFetch: value.lastFetch?.toISOString(),
        isFresh: this.isDataFresh(value.lastFetch)
      };
    });
    return info;
  }
}

// Export singleton instance
export const enhancedApiService = new EnhancedApiService();
export default enhancedApiService;